import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listVideos, toggleLike, toggleWatchLater, resolveFileUrl } from '../lib/api';
import { formatCount } from '../mock';
import { ChevronUp, ChevronDown, Heart, MessageCircle, Share2, Bookmark, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Shorts() {
  const [shorts, setShorts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const trackRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const vids = await listVideos({ category: 'shorts', limit: 50 });
        setShorts(vids);
      } finally { setLoading(false); }
    })();
  }, []);

  const go = (delta) => {
    const next = Math.max(0, Math.min(shorts.length - 1, idx + delta));
    setIdx(next);
    const el = trackRef.current?.children?.[next];
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const current = shorts[idx];

  const onLike = async () => {
    if (!current) return;
    try {
      const { liked } = await toggleLike(current.id);
      setShorts(s => s.map(v => v.id === current.id ? { ...v, liked, likes_count: (v.likes_count || 0) + (liked ? 1 : -1) } : v));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Sign in to like'); }
  };
  const onSave = async () => {
    if (!current) return;
    try { const { saved } = await toggleWatchLater(current.id); setShorts(s => s.map(v => v.id === current.id ? { ...v, saved } : v)); toast.success(saved ? 'Saved' : 'Removed'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (shorts.length === 0) return <div className="text-white/55">No shorts yet. Be the first to upload one from the Upload page.</div>;

  return (
    <div>
      <div className="chip">Shorts</div>
      <h1 className="font-display text-[32px] md:text-[40px] mt-3 leading-none">Quick hits</h1>
      <p className="text-white/60 text-[14px] mt-1">Vertical 9:16 clips. Use the arrows to move between shorts.</p>

      <div className="mt-6 grid lg:grid-cols-[minmax(0,360px)_1fr] gap-6">
        {/* Main player */}
        <div className="relative mx-auto w-full max-w-[360px]">
          <div className="relative rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '9/16' }}>
            {current?.source?.type === 'youtube' ? (
              <iframe
                key={current.id}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${current.source.id}?autoplay=1&loop=1&playlist=${current.source.id}&mute=${muted ? 1 : 0}&controls=0&modestbranding=1&rel=0&playsinline=1`}
                title={current.title}
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <img src={resolveFileUrl(current?.thumb)} alt={current?.title} className="w-full h-full object-cover" />
            )}

            <div className="absolute inset-x-0 bottom-0 p-4 text-white" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.85), rgba(0,0,0,0))' }}>
              <Link to={`/channel/${current.channel?.id}`} className="flex items-center gap-2">
                <img src={resolveFileUrl(current.channel?.avatar)} alt={current.channel?.name} className="w-8 h-8 rounded-full object-cover" />
                <span className="font-semibold text-[13.5px]">{current.channel?.name}</span>
              </Link>
              <div className="text-[14px] mt-2 font-semibold line-clamp-2">{current.title}</div>
              <div className="text-[12px] text-white/70">{formatCount(current.views)} views • {current.uploaded_at}</div>
            </div>
          </div>

          {/* Side actions */}
          <div className="absolute -right-16 md:-right-20 top-1/2 -translate-y-1/2 flex flex-col gap-3">
            <button onClick={() => go(-1)} className="w-12 h-12 rounded-full bg-white/[0.08] hover:bg-white/[0.15] grid place-items-center" aria-label="Prev"><ChevronUp className="w-5 h-5" /></button>
            <button onClick={onLike} className={`w-12 h-12 rounded-full grid place-items-center ${current.liked ? 'bg-[#ff2e63] text-white' : 'bg-white/[0.08] hover:bg-white/[0.15]'}`} aria-label="Like"><Heart className="w-5 h-5" /></button>
            <div className="text-[11px] text-center text-white/60">{formatCount(current.likes_count || 0)}</div>
            <Link to={`/watch/${current.id}`} className="w-12 h-12 rounded-full bg-white/[0.08] hover:bg-white/[0.15] grid place-items-center" aria-label="Comments"><MessageCircle className="w-5 h-5" /></Link>
            <button onClick={onSave} className={`w-12 h-12 rounded-full grid place-items-center ${current.saved ? 'bg-[#22d3ee] text-black' : 'bg-white/[0.08] hover:bg-white/[0.15]'}`} aria-label="Save"><Bookmark className="w-5 h-5" /></button>
            <button onClick={() => { navigator.clipboard.writeText(window.location.origin + '/watch/' + current.id); toast.success('Link copied'); }} className="w-12 h-12 rounded-full bg-white/[0.08] hover:bg-white/[0.15] grid place-items-center" aria-label="Share"><Share2 className="w-5 h-5" /></button>
            <button onClick={() => setMuted(m => !m)} className="w-12 h-12 rounded-full bg-white/[0.08] hover:bg-white/[0.15] grid place-items-center" aria-label="Mute">{muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</button>
            <button onClick={() => go(1)} className="w-12 h-12 rounded-full bg-white/[0.08] hover:bg-white/[0.15] grid place-items-center" aria-label="Next"><ChevronDown className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Up next rail */}
        <div>
          <h2 className="row-title text-white mb-3">Up next</h2>
          <div ref={trackRef} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {shorts.map((s, i) => (
              <button key={s.id} onClick={() => setIdx(i)} className={`relative rounded-xl overflow-hidden text-left group ${i === idx ? 'ring-2 ring-[#ff2e63]' : 'ring-1 ring-white/10'}`} style={{ aspectRatio: '9/16' }}>
                <img src={resolveFileUrl(s.thumb)} alt={s.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.8), rgba(0,0,0,0) 60%)' }} />
                <div className="absolute bottom-0 left-0 right-0 p-2">
                  <div className="text-white text-[12px] font-semibold line-clamp-2">{s.title}</div>
                  <div className="text-white/70 text-[11px]">{formatCount(s.views)} views</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
